import { useState } from 'react';

export default function Footer() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d60bo8giku00ff9vl2o0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <footer id="contact" className="bg-black rounded-t-[32px] mt-20">
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="grid grid-cols-3 gap-16 mb-16">
          <div>
            <h3 className="text-5xl font-light text-white mb-4">Get In Touch</h3>
            <p className="text-gray-400 text-base mb-12">
              We're here to answer your questions
            </p>
            
            <div className="space-y-6">
              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                  <i className="ri-mail-line text-sm"></i>
                  <span>Email Address</span>
                </div>
                <a 
                  href="mailto:help@euripusdoo.com" 
                  className="text-white text-lg hover:underline cursor-pointer"
                >
                  help@euripusdoo.com
                </a>
              </div>
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wider mb-6">CONTACT</div>
            <div className="space-y-6">
              <div>
                <div className="text-gray-400 text-sm mb-2 flex items-center gap-2">
                  <i className="ri-phone-line text-base"></i>
                  <span>Phone Number</span>
                </div>
                <a 
                  href="tel:+17815127898" 
                  className="text-white text-lg cursor-pointer hover:text-gray-300 transition-colors"
                >
                  +1 7815127898
                </a>
              </div>
              <div>
                <div className="text-gray-400 text-sm mb-2 flex items-center gap-2">
                  <i className="ri-mail-line text-base"></i>
                  <span>Email</span>
                </div>
                <a 
                  href="mailto:help@euripusdoo.com" 
                  className="text-white text-lg hover:underline cursor-pointer"
                >
                  help@euripusdoo.com
                </a>
              </div>
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wider mb-6 flex items-center gap-2">
              <i className="ri-map-pin-line text-sm"></i>
              <span>LOCATION</span>
            </div>
            <div className="space-y-6">
              <div>
                <div className="text-gray-400 text-sm mb-2">Address</div>
                <div className="text-white text-base leading-relaxed">
                  Molizanskih Hrvata 9b<br />
                  Makarska, 21300<br />
                  Croatia
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-12 mb-12">
          <h4 className="text-2xl font-semibold text-white mb-8">Send us a message</h4>
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-gray-400 text-sm mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-gray-400 text-sm mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div>
              <label htmlFor="message" className="block text-gray-400 text-sm mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-white/30 transition-colors resize-none"
                placeholder="Your message (max 500 characters)"
              />
              <div className="text-gray-500 text-xs mt-1 text-right">
                {formData.message.length}/500
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 bg-white text-black font-medium rounded-full hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            {submitStatus === 'success' && (
              <p className="text-green-400 text-sm">Message sent successfully!</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-400 text-sm">Failed to send message. Please try again.</p>
            )}
          </form>
        </div>

        <div className="border-t border-white/10 pt-10">
          <div className="flex items-center justify-between text-sm">
            <div className="text-gray-400">
              © 2025 EURIPUS d.o.o. All rights reserved
            </div>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:underline cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
